import { useEffect, useRef, useState } from "react";
import { focusIsLost } from "../a11y";
import { api, ApiError } from "../api";
import { formatHours } from "../format";
import { QUICK_PLAY_OUTCOME_NOTE } from "../results";
import { isValidEmail, resultsEmailMessage } from "../signup";
import { numberWord } from "../trend";
import { ScoringDetails } from "./Scoring";
import type { DebriefView } from "../types";

/** How many cause-and-effect threads to show before "Show more". */
const CHAINS_SHOWN = 6;

/**
 * Outcome screen and debrief (06 §4, 19 §4.11), then the playtest questions (06 §6–7).
 * This is the only screen that reveals hidden state, and only after the run has ended.
 */
export function Outcome({ runId, onExit, orgName }: { runId: string; onExit: () => void; orgName: string }) {
  const [d, setD] = useState<DebriefView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [showAllChains, setShowAllChains] = useState(false);

  const title = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    api.debrief(runId).then((r) => setD(r.debrief)).catch((e: Error) => setError(e.message));
  }, [runId]);
  // The debrief arrives after the last week ends; if nothing else holds focus, start the reader at its heading.
  useEffect(() => {
    const a = document.activeElement;
    if (d && (focusIsLost() || a?.classList.contains("stage__content"))) title.current?.focus();
  }, [d]);

  if (error) return <section className="ending"><p role="alert">{error}</p></section>;
  if (!d) return <section className="ending" aria-busy="true"><p className="muted" role="status">Gathering the last few weeks…</p></section>;

  const first = d.weeks[0];
  const last = d.weeks[d.weeks.length - 1];
  const maxH = Math.max(...d.weeks.map((w) => w.hours), 1);
  const span = d.weeks.length === 1 ? "One week" : `${numberWord(d.weeks.length)} weeks`;

  return (
    <section className="ending" aria-labelledby="end-title">
      <h2 id="end-title" className="ending__title" ref={title} tabIndex={-1}>{span.charAt(0).toUpperCase() + span.slice(1)} on</h2>
      {d.mode === "quick" && <p className="quick-note">{QUICK_PLAY_OUTCOME_NOTE}</p>}
      {d.ending && <p className="ending__headline">{d.ending.headline}</p>}
      {d.ending?.codas?.map((c, i) => <p key={i} className="ending__coda">{c}</p>)}

      <ResultsByEmail runId={runId} orgName={orgName} />

      <h3>Your weeks</h3>
      {first && last && (
        <p>Week 1: {formatHours(first.hours)}. Week {last.week}: {formatHours(last.hours)}.</p>
      )}
      <ol className="hours-bars" aria-label="Hours worked each week">
        {d.weeks.map((w) => (
          <li key={w.week}>
            <span className="hours-bars__label">Week {w.week}</span>
            {/* Visual only: the hours, and the share on the business (striped), are written out beside the bar. */}
            <span className="hours-bars__bar" style={{ width: `${(w.hours / maxH) * 100}%` }} aria-hidden="true">
              <span className="hours-bars__on" style={{ width: `${(w.hoursOn / Math.max(w.hours, 1)) * 100}%` }} />
            </span>
            <span className="num">{formatHours(w.hours)} <span className="muted">({formatHours(w.hoursOn)} on the business; {w.escalations} brought to you; {w.resolvedWithoutYou} sorted without you)</span></span>
          </li>
        ))}
      </ol>

      {d.hoursBoughtBack.length > 0 && (
        <>
          <h3>Hours bought back each week</h3>
          <ul>{d.hoursBoughtBack.map((h) => <li key={h.label}>{h.label}: {formatHours(h.hours)}</li>)}</ul>
        </>
      )}

      {d.objectives.length > 0 && (
        <>
          <h3>What you set out to do</h3>
          <ul>{d.objectives.map((o) => <li key={o.id}><span aria-hidden="true">{o.done ? "✓" : "–"}</span> {o.text} <span className="muted">{o.done ? "(done)" : "(not yet)"}</span></li>)}</ul>
        </>
      )}

      {d.measures.length > 0 && (
        <>
          <h3>What was really happening</h3>
          <table className="acc-table">
            <caption className="visually-hidden">What was really happening, measured at the end</caption>
            <tbody>
            {d.measures.map((m) => <tr key={m.id}><th scope="row">{m.label}</th><td className="num">{m.value}</td></tr>)}
          </tbody></table>
        </>
      )}

      {d.processes.length > 0 && (
        <>
          <h3>Processes, as they really are</h3>
          <ul>{d.processes.map((p) => (
            <li key={p.name}>{p.name}: owner {p.owner ?? "none"}, adoption {Math.round(p.adoption * 100)}%
              {p.partnerBinding ? ", binding on partners" : ", partners exempt"}{p.exceptions ? `, ${p.exceptions} exception${p.exceptions > 1 ? "s" : ""}` : ""}</li>
          ))}</ul>
        </>
      )}

      {d.chains.length > 0 && (
        <section className="why" aria-labelledby="why-title">
          <h3 id="why-title">Why did this happen?</h3>
          <p className="muted why__intro">
            Things that happened later in the run, each traced back to a choice you made earlier. The strongest threads come
            first. Open one to follow it step by step. This isn't part of your score: it shows cause and effect.
          </p>
          <ul className="chains">
            {d.chains.slice(0, showAllChains ? d.chains.length : CHAINS_SHOWN).map((c, i) => (
              <li key={i}>
                <button type="button" className="chain__toggle" aria-expanded={open === i} aria-controls={open === i ? `chain-${i}` : undefined}
                  onClick={() => setOpen(open === i ? null : i)}>
                  <span className="chain__outcome">Week {c.outcome.week}: {c.outcome.label}</span>
                  <span className="chain__root">
                    Traces back to week {c.root.week}, when you {c.root.choice} <span className="muted">({c.root.event})</span>
                    {c.decisions > 1 && <> and {c.decisions - 1} later choice{c.decisions > 2 ? "s" : ""} of yours</>}
                  </span>
                </button>
                {open === i && (
                  <ol className="chain" id={`chain-${i}`}>
                    {c.causes.map((x, j) => (
                      <li key={j} className={x.kind === "decision" ? "chain__step chain__step--you" : "chain__step"}>
                        <span className="chain__week">Week {x.week}</span>{" "}
                        {x.kind === "decision"
                          ? <><strong>You {x.label}</strong>{x.event && <span className="muted"> ({x.event})</span>}</>
                          : <>Then: {x.label}</>}
                      </li>
                    ))}
                    <li className="chain__step chain__step--result"><span className="chain__week">Week {c.outcome.week}</span> <strong>Result:</strong> {c.outcome.label}</li>
                  </ol>
                )}
              </li>
            ))}
          </ul>
          {d.chains.length > CHAINS_SHOWN && (
            <button type="button" className="btn btn--quiet" onClick={() => setShowAllChains((v) => !v)} aria-expanded={showAllChains}>
              {showAllChains ? "Show fewer" : `Show ${d.chains.length - CHAINS_SHOWN} more`}
            </button>
          )}
        </section>
      )}

      <section className="ending__scoring" aria-labelledby="scored-title">
        <h3 id="scored-title">How this was scored</h3>
        <ScoringDetails scoring={d.scoring} headingLevel={4} />
      </section>

      <Feedback runId={runId} />
      <div className="row-actions"><button type="button" className="btn btn--primary" onClick={onExit}>Back to the start</button></div>
    </section>
  );
}

/** The two validation questions (06 §6–7), plus anything else they want to say. */
function Feedback({ runId }: { runId: string }) {
  const [real, setReal] = useState("");
  const [why, setWhy] = useState("");
  const [other, setOther] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const thanks = useRef<HTMLParagraphElement>(null);
  // The form is replaced by the thank-you note: move focus to it so it is not lost.
  useEffect(() => { if (sent) thanks.current?.focus(); }, [sent]);
  if (sent) return <p className="panel-note" role="status" ref={thanks} tabIndex={-1}>Thank you. Your answers have been saved.</p>;
  const submit = () => {
    setSending(true);
    setError(null);
    api.feedback(runId, { uncomfortablyReal: real, understoodHours: why, other })
      .then(() => setSent(true))
      .catch((e: unknown) => setError(e instanceof Error ? `Your answers were not saved: ${e.message}` : "Your answers were not saved."))
      .finally(() => setSending(false));
  };
  return (
    <form className="feedback" aria-labelledby="feedback-title" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <h3 id="feedback-title">Two questions</h3>
      <label>Did anything happen in there that felt uncomfortably like your firm?
        <textarea value={real} onChange={(e) => setReal(e.target.value)} rows={3} />
      </label>
      <label>Did you understand why your working hours changed?
        <textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={3} />
      </label>
      <label>Anything else?
        <textarea value={other} onChange={(e) => setOther(e.target.value)} rows={2} />
      </label>
      <div role="alert">{error && <p className="form__error">{error}</p>}</div>
      <button type="submit" className="btn btn--primary" disabled={sending || (!real && !why && !other)}>{sending ? "Sending…" : "Send"}</button>
    </form>
  );
}

/**
 * "Get your results by email" (19 §4.11). Prefilled from sign-up. The outcome is announced in words and receives
 * focus; it never says "sent" when the request was only saved for later.
 */
function ResultsByEmail({ runId, orgName }: { runId: string; orgName: string }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const touched = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const note = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    api.me().then(({ user }) => { if (!touched.current && user.email) setEmail(user.email); }).catch(() => undefined);
  }, []);
  useEffect(() => { if (done) note.current?.focus(); }, [done]);

  const send = async () => {
    setError(null);
    const to = email.trim();
    if (!isValidEmail(to)) {
      setError("Please enter a valid email address.");
      input.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const r = await api.resultsEmail(runId, to, consent);
      setDone(resultsEmailMessage(r.status, r.email));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Your request was not saved. Please try again.");
      input.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="results-email" aria-labelledby="results-email-title">
      <h3 id="results-email-title">Get your results by email</h3>
      {done ? (
        <p className="results-email__done" role="status" ref={note} tabIndex={-1}>{done}</p>
      ) : (
        <form noValidate onSubmit={(e) => { e.preventDefault(); void send(); }}>
          <p className="muted">A summary of how it went, to keep or to share.</p>
          <div className="results-email__row">
            <label htmlFor="results-email">Email</label>
            <input id="results-email" type="email" autoComplete="email" inputMode="email" value={email} ref={input}
              onChange={(e) => { touched.current = true; setEmail(e.target.value); if (error) setError(null); }}
              aria-invalid={error ? true : undefined} aria-describedby={error ? "results-email-error" : undefined} />
          </div>
          <label className="check">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>I'm happy for {orgName} to get in touch to talk through how it went.</span>
          </label>
          <div role="alert">{error && <p className="form__error" id="results-email-error">{error}</p>}</div>
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? "Sending…" : "Send"}</button>
        </form>
      )}
    </section>
  );
}
